import { ZIndex } from "@/constants";
import { useContextOverlay } from "@/hooks/useContextTree";
import { useDevice } from "@/hooks/useDevice";
import { useLayoutColors } from "@/hooks/useLayoutColors";
import { Button } from "@/components/ui/button";
import { XIcon } from "lucide-react";
import { createContext, useContext } from "react";
import { createPortal } from "react-dom";

const DialogLayerContext = createContext(0);

export function useDialogLayer() {
  return useContext(DialogLayerContext);
}

/**
 * What it wraps is a layer over the page, as a dialog is - a sheet up from
 * the bottom, say - so a popper opened in it is drawn over it rather than
 * under it.
 */
export function DialogLayer({ children }: { children: React.ReactNode }) {
  const parentLayer = useContext(DialogLayerContext);
  return (
    <DialogLayerContext.Provider value={parentLayer + 1}>
      {children}
    </DialogLayerContext.Provider>
  );
}

export function Dialog({
  renderKey = null,
  isOpen,
  onClose,
  isEscapable = true,
  isConstrained = false,
  isFullScreen = false,
  minWidth: minWidthOverride,
  maxWidth: maxWidthOverride,
  fillsHeight = false,
  fillsWindow = false,
  children,
}: {
  renderKey?: string | null;
  isOpen: boolean;
  onClose: () => void;
  isEscapable?: boolean;
  /** Constrained mode: maxWidth ~1120px, constrained height with padding */
  isConstrained?: boolean;
  /** Full screen mode: takes up entire viewport with no padding */
  isFullScreen?: boolean;
  /** Override the default minimum width of the dialog */
  minWidth?: string;
  /** A constrained dialog's widest, in place of ~1120px: as wide as that, or the window less its padding. */
  maxWidth?: string;
  /** As tall as a full-screen dialog - the window less its margins - however wide it is: what it holds fills it. */
  fillsHeight?: boolean;
  /** The whole window, edge to edge, as a narrow window's dialog is - full screen, with no margin round it. */
  fillsWindow?: boolean;
  children: React.ReactElement;
}) {
  const colors = useLayoutColors();
  // A phone has no room round a dialog, nor a key or a backdrop to shut it
  // with: it is the whole screen, with a bar to close it by at its top. A
  // window as narrow on a computer has no more room, and no backdrop left to
  // click: its dialog is the whole window, with the same bar.
  const { isNarrow: isNarrowWindow } = useDevice();
  const isNarrow = isNarrowWindow || fillsWindow;
  const parentLayer = useContext(DialogLayerContext);
  const currentLayer = parentLayer + 1;
  const dialogZIndex = ZIndex.APP.DIALOG + (currentLayer - 1) * 200;

  const { Scope } = useContextOverlay({
    isOpen,
    onClose,
    bridge: true,
    escapeable: isEscapable,
    autoFocus: true,
    contentKey: renderKey,
  });

  const actualOnClose = onClose;

  const padding = isFullScreen ? 16 : isConstrained ? 64 : 16;

  const widest = maxWidthOverride
    ? `min(${maxWidthOverride}, calc(100vw - ${padding * 2}px))`
    : "1120px";

  const width = isFullScreen
    ? `calc(100vw - ${padding * 2}px)`
    : isConstrained
      ? widest
      : "max-content";

  const maxWidth = isFullScreen ? `calc(100vw - ${padding * 2}px)` : widest;

  // A full screen's margins, top and bottom - for one that fills the height too.
  const tallest = `calc(100vh - ${16 * 2}px)`;

  const height = isFullScreen
    ? `calc(100vh - ${padding * 2}px)`
    : fillsHeight
      ? tallest
      : "auto";

  const maxHeight = fillsHeight ? tallest : `calc(100vh - ${padding * 2}px)`;

  const minWidth =
    minWidthOverride ??
    (isFullScreen ? `calc(100vw - ${padding * 2}px)` : "800px");

  const borderRadius = isNarrow ? 0 : "12px";

  if (!isOpen) return null;

  return createPortal(
    <DialogLayerContext.Provider value={currentLayer}>
      <div
        onClick={() => {
          if (isEscapable) {
            actualOnClose();
          }
        }}
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: colors.dialogOverlay,
          backdropFilter: "blur(2px)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          overflowY: "hidden",
          zIndex: dialogZIndex,
        }}
      >
        <Scope
          tabIndex={-1}
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          style={{
            position: "relative",
            display: "flex",
            flexWrap: "nowrap",
            flexDirection: "column",
            alignSelf: "center",
            flexShrink: 0,
            boxSizing: "border-box",
            ...(isNarrow
              ? {
                  width: "100vw",
                  height: "100dvh",
                  paddingTop: "env(safe-area-inset-top)",
                  paddingBottom: "env(safe-area-inset-bottom)",
                }
              : {
                  minWidth: minWidth,
                  width: width,
                  maxWidth: maxWidth,
                  height: height,
                  maxHeight: maxHeight,
                  border: `1px solid ${"var(--border)"}`,
                  outline: `4px solid ${`color-mix(in srgb, ${"var(--popover)"} 100%, transparent)`}`,
                  boxShadow: "var(--shadow-xl)",
                  padding: 0,
                }),
            overflowY: "auto",
            backgroundColor: colors.dialog,
            borderRadius: borderRadius,
          }}
        >
          {isNarrow ? (
            <div className="flex justify-end shrink-0 pl-1 pr-1 [border-bottom-width:1px] [border-bottom-style:solid] border-border">
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label="Close"
                data-testid="dialog-close"
                onClick={actualOnClose}
              >
                <XIcon />
              </Button>
            </div>
          ) : null}
          {isNarrow ? (
            // The whole screen, so what a dialog draws fills it: one whose
            // body grows keeps its footer on the screen's foot, not under
            // whatever it had to say.
            <div
              data-testid="dialog-body"
              className="flex-1 min-h-0 flex flex-col *:grow *:min-h-0"
            >
              {children}
            </div>
          ) : (
            children
          )}
        </Scope>
      </div>
    </DialogLayerContext.Provider>,
    document.body,
  );
}
